import * as React from 'react';
import * as S from './styles';
import * as T from './types';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from 'store/types';
import { capitalizeFirstLetter } from '../../state/utils';
import RadioButtonList from "components/RadioButtonList";
import FilterCheck from 'components/Filter';
import { fullDateRegex, validateDateFormat, validateInputDateFormat } from 'features/Dealers/Listings/Data/state/utils';
import { ICustomFilter } from '../SearchReviews/types';

const STAR_RATE = ['5', '4', '3', '2', '1'];
const FIXED_DATES = ['start', 'end'] as const;
type CheckFilterType = "providers" | "stars";
type CustomDateType = typeof FIXED_DATES[number];

const ReviewFilters: React.FC<T.IProps> = (props) => {

    const dateIndexIncome = props.currentFilters.find(cf => cf.attribute === "lastDays")?.filters[0];
    const customDatesIncome = props.currentFilters.find(cf => cf.attribute === "customDates")?.filters;
    const customDatesIncomeValue = customDatesIncome && customDatesIncome.length === 2 ? {start: customDatesIncome[0], end: customDatesIncome[1]} : undefined

    const {
        providersRating,
        reviewsConfig,
    } = useSelector((rootState: RootState) => {
        return {
            providersRating: rootState.reviews.providersRating,
            reviewsConfig: rootState.reviews.reviewsConfig,
        }
    }, shallowEqual);

    const componentRef = React.useRef<HTMLDivElement|null>(null);
    const [providerFilters, setProviderFilters] = React.useState<string[]>(props.currentFilters.find(cf => cf.attribute === "provider")?.filters ?? []);
    const [starFilters, setStarFilters] = React.useState<string[]>(props.currentFilters.find(cf => cf.attribute === "rating")?.filters ?? []);
    const [dateIndex, setDateIndex] = React.useState<number | undefined>(dateIndexIncome !== undefined ? parseInt(dateIndexIncome) : undefined);
    const [customDatesSelected, setCustomDatesSelected] = React.useState<boolean>(customDatesIncome && customDatesIncome.length === 2 && dateIndexIncome === undefined ? true : false);
    const [customDates, setCustomDates] = React.useState<T.ICustomDates>(customDatesIncomeValue ?? {});
    const [customDateFilters, setCustomDateFilters] = React.useState<T.ICustomDates>(customDatesIncomeValue ?? {});

    const handleClickOutside = (e: any) => {
        if (componentRef && componentRef.current) {
            const ref: (HTMLDivElement|null) = componentRef.current;
            if (!ref.contains(e.target)) props.setOpenFilers(false);
        }
    }

    React.useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    const returnDatesList = () => {
        return reviewsConfig?.topReviews.filters.dates.map((dateFilter, index) => {
            return (
                {
                    id: `${index}`,
                    name: dateFilter.displayName,
                    enable: true,
                    itemMessage: ""
                }
            );
        });
    }

    const handleChangeFilters = (value: string, type: string, add: boolean) => {
        const filter = value.split(" ")[0].toUpperCase();
        if (type === "providers") {
            const index = providerFilters.findIndex(providerFilter => providerFilter === filter);
            if (index >= 0) setProviderFilters([
                ...providerFilters.slice(0, index),
                ...providerFilters.slice(index + 1)
            ]);
            else setProviderFilters([...providerFilters, filter]);
        } else {
            const index = starFilters.findIndex(starFilter => starFilter === filter);
            if (index >= 0) setStarFilters([
                ...starFilters.slice(0, index),
                ...starFilters.slice(index + 1)
            ]);
            else setStarFilters([...starFilters, filter]);
        }
    }

    const handleChangeDates = (e: React.ChangeEvent<HTMLInputElement>, type: CustomDateType) => {
        const value = e.target.value;
        const date = value.split("/");
        if (date.length === 3 && date[2].length === 4) {
            const currentDate = new Date();
            const inputDate = new Date(value);
            if (inputDate > currentDate) return;
            if (date.every(d => d !== "") && customDates.start && customDates.end) {
                const startValue = type === "start" ? value : customDates.start;
                const endValue = type === "end" ? value : customDates.end;
                const start = startValue.split("/");
                const end = endValue.split("/");
                if (start.every(d => d !== "") && start[2]?.length === 4 && end.every(d => d !== "") && end[2]?.length === 4) {
                    const startDate = new Date(startValue);
                    const endDate = new Date(endValue);
                    if (startDate > endDate) return;
                }
            }
        }
        if (validateInputDateFormat(value) !== undefined) {
            let newCustomDates = { ...customDates };
            newCustomDates[type] = value;
            setCustomDates(newCustomDates);
        }
    }

    const handleAddCustomDates = () => {
        const newCustomDateFilters: T.ICustomDates = {};
        FIXED_DATES.map(fixedDate => {
            const formattedDate = customDates[fixedDate]?.split("/").map(d => String(d).padStart(2, "0")).join("/");
            if (fullDateRegex(formattedDate)) newCustomDateFilters[fixedDate] = formattedDate;
        });
        if (Object.values(newCustomDateFilters).every(d => d)) setCustomDateFilters(newCustomDateFilters);
    }

    React.useEffect(() => {
        let filters: ICustomFilter[] = [];
        filters.push({attribute: "provider", filters: providerFilters});
        filters.push({attribute: "rating", filters: starFilters});
        if (reviewsConfig && dateIndex !== undefined) filters.push({attribute: "lastDays", filters: [dateIndex.toString()]});
        const customDateValues = Object.values(customDateFilters);
        if (customDatesSelected && customDateValues.length && customDateValues.every(d => d.split("/")[2]?.length === 4)) filters.push({attribute: "customDates", filters: customDateValues});
        props.setCurrentFilters(filters);
    }, [providerFilters.length, starFilters.length, dateIndex, customDatesSelected, JSON.stringify(customDateFilters)]);

    const renderFilter = (from: CheckFilterType, id: string, label: string) => {
        const checked = from === "providers" ?
            providerFilters.some(providerFilter => providerFilter === id) :
            starFilters.some(starFilter => starFilter === id);
        return (
            <FilterCheck
                key={id}
                label={label}
                type={from}
                onChange={handleChangeFilters}
                checked={checked}
                styleRoot={{ padding: '0px 0px', height: "29px" }}
                styledCheckbox={{
                    borderRadius: '5px',
                    border: checked ? 'none' : 'solid 2px rgba(189, 189, 189, 0.5)',
                    background: checked ? '#2b3a66' : '#fff',
                    width: checked ? '22px' : '19.5px',
                    height: checked ? '22px' : '19.5px',
                }}
                styleLabel={{
                    fontSize: '14px',
                    color: '#000',
                    marginLeft: '9px'
                }}
                stroke='#fff'
            />
        );
    }

    const renderCustomDate = (type: CustomDateType) => {
        return (
            <S.CustomDateRow>
                <S.CustomDateLabel>
                    {type}
                </S.CustomDateLabel>
                <S.CustomDateInput
                    placeholder={`mm/dd/yyyy`}
                    value={customDates[type]}
                    onChange={(e) => handleChangeDates(e, type)}
                    onBlur={handleAddCustomDates}
                    onKeyPress={validateDateFormat}
                    onPaste={validateDateFormat}
                    disabled={!customDatesSelected}
                />
            </S.CustomDateRow>
        )
    }

    return (
        <S.Root
            ref={componentRef}
        >
            <S.ColumnFilters widthValue={'175px'} >
                <S.FilterTitle>
                    Provider
                </S.FilterTitle>
                <S.FilterList>
                    {
                        providersRating?.providersRating.map((providerRating, index) => {
                            return renderFilter("providers", providerRating.provider, capitalizeFirstLetter(providerRating.provider));
                        })
                    }
                </S.FilterList>
            </S.ColumnFilters>
            <S.VerticalDivider />
            <S.ColumnFilters widthValue={'158px'} >
                <S.FilterTitle>
                    Star Rating
                </S.FilterTitle>
                <S.FilterList>
                    {
                        STAR_RATE.map((star, index) => {
                            return renderFilter("stars", star, star === '1' ? `1 star` : `${star} stars`);
                        })
                    }
                </S.FilterList>
            </S.ColumnFilters>
            <S.VerticalDivider />
            <S.ColumnFilters widthValue={'191px'} >
                <S.FilterTitle>
                    Date
                </S.FilterTitle>
                <S.FilterList>
                    <RadioButtonList
                        name="rb-reviews-filter-dates"
                        RadioButtonListItems={returnDatesList() ?? []}
                        onSelectedItem={(selectedValue) => { setDateIndex(selectedValue); setCustomDatesSelected(false) }}
                        radioOptionsStyle={{ fontWeight: "normal" }}
                        radioOptionStyle={{ marginBottom: "5px" }}
                        radioStyle={{ margin: "0px 9px 0px 0px" }}
                        radioLabelStyle={{ fontSize: "14px", marginLeft: "0px" }}
                        selected={customDatesSelected ? undefined : `${dateIndex}`}
                    />
                </S.FilterList>
                <S.HorizontalDivider />
                <S.CustomDate>
                    <RadioButtonList
                        name="rb-reviews-filter-dates"
                        RadioButtonListItems={[{
                            id: `${0}`,
                            name: `Enter Date Range`,
                            enable: true,
                            itemMessage: ""
                        }]}
                        onSelectedItem={(selectedValue) => { setDateIndex(undefined); setCustomDatesSelected(true) }}
                        radioOptionsStyle={{ fontWeight: "normal" }}
                        radioOptionStyle={{ marginBottom: "5px" }}
                        radioStyle={{ margin: "0px 9px 0px 0px" }}
                        radioLabelStyle={{ fontSize: "14px", marginLeft: "0px" }}
                        selected={customDatesSelected ? `0` : undefined}
                    />
                    {renderCustomDate("start")}
                    {renderCustomDate("end")}
                </S.CustomDate>
            </S.ColumnFilters>
        </S.Root>
    );
}

export default ReviewFilters;
